import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  deleteHypothesis,
  listHypotheses,
  pauseAllHypotheses,
  STATUS_META,
  type Hypothesis,
  type HypothesisStatus,
} from "../api/hypotheses.js";
import { api, ApiError } from "../api/client.js";
import { MetricCard } from "../components/MetricCard.js";
import { MetricSection } from "../components/MetricSection.js";
import { WhatToDoBanner } from "../components/WhatToDoBanner.js";

interface ListKpisResponse {
  kpis: Array<{
    metric: string;
    current: number | null;
    previous: number | null;
    sparkline: Array<{ day: string; value: number | null }>;
  }>;
}

const STATUS_FILTERS: Array<{ id: HypothesisStatus | "all"; label: string }> = [
  { id: "all", label: "Все" },
  { id: "draft", label: "Черновики" },
  { id: "pending_review", label: "На ревью" },
  { id: "approved", label: "Одобрены" },
  { id: "active", label: "Активные" },
  { id: "paused", label: "Пауза" },
  { id: "verdict_pending", label: "Ждут verdict" },
  { id: "killed", label: "Убитые" },
  { id: "scaled", label: "Масштаб" },
];

const formatMSK = (ts: string) =>
  new Date(ts).toLocaleString("ru-RU", {
    timeZone: "Europe/Moscow",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

export function HypothesesPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<HypothesisStatus | "all">("all");
  const [entries, setEntries] = useState<Hypothesis[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pausing, setPausing] = useState(false);
  const [pauseResult, setPauseResult] = useState<string | null>(null);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [autoGenResult, setAutoGenResult] = useState<string | null>(null);
  const [bar, setBar] = useState<ListKpisResponse | null>(null);

  // KPI-bar: сводка по списку (Task 22). Backend stub в Task 14a, реальные числа в 14b.
  useEffect(() => {
    api<ListKpisResponse>("/api/metrics/list-kpis/hypotheses")
      .then(setBar)
      .catch(() => setBar(null));
  }, []);

  function load() {
    setEntries(null);
    setError(null);
    listHypotheses(filter === "all" ? {} : { status: filter })
      .then((d) => setEntries(d.entries))
      .catch((e) => setError(e instanceof Error ? e.message : "Unknown error"));
  }

  useEffect(() => {
    load();
  }, [filter]);

  async function handleAutoGenerate() {
    if (
      !confirm(
        "Авто-сгенерировать draft гипотез из топ-5 SKU × 3 креатива (= 15 драфтов)? Тексты будут placeholder'ы — придётся править в wizard'е перед approve.",
      )
    ) {
      return;
    }
    setAutoGenerating(true);
    setAutoGenResult(null);
    try {
      const r = await api<{ created_count: number; skipped_count: number; source_skus: Array<{ name: string }> }>(
        "/api/hypotheses/auto-generate",
        { method: "POST", body: JSON.stringify({ top: 5, creatives_per_sku: 3 }) },
      );
      setAutoGenResult(
        `Создано ${r.created_count} draft'ов из ${r.source_skus.length} SKU (${r.source_skus.map((s) => s.name).slice(0, 3).join(", ")}${r.source_skus.length > 3 ? "..." : ""}). Skipped: ${r.skipped_count}.`,
      );
      load();
    } catch (e) {
      setAutoGenResult(`Ошибка: ${e instanceof ApiError ? e.message : (e as Error).message}`);
    } finally {
      setAutoGenerating(false);
    }
  }

  async function handlePauseAll() {
    if (!confirm("Поставить на паузу ВСЕ активные гипотезы и suspend в Я.Директ? Существующие 3 ЕПК не затрагиваются.")) {
      return;
    }
    setPausing(true);
    setPauseResult(null);
    try {
      const r = await pauseAllHypotheses({ reason: "manual pause-all from UI" });
      setPauseResult(`OK ${r.ok} / Failed ${r.failed} из ${r.total}`);
      load();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setPauseResult(`Ошибка: ${msg}`);
    } finally {
      setPausing(false);
    }
  }

  return (
    <div>
      {/* Action banner — что Денису делать прямо сейчас */}
      <WhatToDoBanner entries={entries} />

      {bar && bar.kpis.length > 0 && (
        <MetricSection title="Сводка" cols={4}>
          {bar.kpis.slice(0, 4).map((k, i) => (
            <MetricCard
              key={i}
              metricId="cost_rub"
              current={k.current ?? null}
              previous={k.previous ?? null}
              comparisonLabel="vs прошлый период"
              sparkline={[]}
            />
          ))}
        </MetricSection>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
        <div>
          <h2 style={{ margin: 0, fontSize: "1.5rem" }}>Гипотезы</h2>
          <p style={{ color: "var(--text-dim)", margin: "0.25rem 0 0" }}>
            Узкие тест-кампании: 1 товар × 1 аудитория × 1 хук. Существующие 3 ЕПК не затрагиваются.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={handleAutoGenerate}
            disabled={autoGenerating}
            style={{
              padding: "0.5rem 0.9rem",
              background: "var(--bg-elevated)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              cursor: autoGenerating ? "wait" : "pointer",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
            title="Создать 5×3=15 draft гипотез из топ-SKU"
          >
            {autoGenerating ? "..." : "🤖 Авто-генерация"}
          </button>
          <button
            onClick={handlePauseAll}
            disabled={pausing}
            style={{
              padding: "0.5rem 0.9rem",
              background: "var(--bg-elevated)",
              color: "var(--red)",
              border: "1px solid var(--red)",
              borderRadius: 8,
              cursor: pausing ? "wait" : "pointer",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
            title="Suspend всех активных гипотез + перевод в paused (большая красная кнопка)"
          >
            {pausing ? "Пауза..." : "🟥 Pause all"}
          </button>
          <button
            onClick={() => navigate("/hypotheses/new")}
            style={{
              padding: "0.5rem 0.9rem",
              background: "var(--green)",
              color: "var(--bg)",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            + Создать
          </button>
        </div>
      </div>

      {pauseResult && (
        <div
          style={{
            padding: "0.6rem 0.9rem",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            color: "var(--text-dim)",
            fontSize: "0.85rem",
            marginBottom: "1rem",
          }}
        >
          {pauseResult}
        </div>
      )}

      {autoGenResult && (
        <div
          style={{
            padding: "0.6rem 0.9rem",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            color: "var(--text-dim)",
            fontSize: "0.85rem",
            marginBottom: "1rem",
          }}
        >
          {autoGenResult}
        </div>
      )}

      <div style={{ display: "flex", gap: "0.4rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              padding: "0.35rem 0.7rem",
              background: filter === f.id ? "var(--text)" : "var(--bg-elevated)",
              color: filter === f.id ? "var(--bg)" : "var(--text-dim)",
              border: "1px solid var(--border)",
              borderRadius: 16,
              cursor: "pointer",
              fontSize: "0.78rem",
              fontWeight: filter === f.id ? 600 : 500,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div style={{ color: "var(--red)" }}>Ошибка: {error}</div>}
      {!error && !entries && <div style={{ color: "var(--text-dim)" }}>Загрузка...</div>}

      {entries && entries.length === 0 && (
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            color: "var(--text-dim)",
          }}
        >
          {filter === "all"
            ? "Гипотез ещё нет. Жми «+ Создать», чтобы запустить первую тест-кампанию."
            : `В этом статусе пусто. Поменяй фильтр или создай новую.`}
        </div>
      )}

      {entries && entries.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Имя</th>
              <th>Статус</th>
              <th>Wave / Persona</th>
              <th className="right">Бюджет/день</th>
              <th className="right">Цели</th>
              <th>Создана</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.map((h) => {
              const meta = STATUS_META[h.status];
              return (
                <tr key={h.id}>
                  <td onClick={() => navigate(`/hypotheses/${h.id}`)} style={{ cursor: "pointer" }}>
                    <Link to={`/hypotheses/${h.id}`} style={{ color: "var(--text)", fontWeight: 600, textDecoration: "none" }}>
                      {h.name}
                    </Link>
                  </td>
                  <td onClick={() => navigate(`/hypotheses/${h.id}`)} style={{ cursor: "pointer" }}>
                    <span className={`badge badge-${meta.color}`}>{meta.label}</span>
                  </td>
                  <td className="dim mono" onClick={() => navigate(`/hypotheses/${h.id}`)} style={{ cursor: "pointer", fontSize: "0.72rem" }}>
                    {h.wave_id ? <div>{h.wave_id}</div> : <div>—</div>}
                    {h.persona_label && <div style={{ color: "var(--text-dim)" }}>{h.persona_label}</div>}
                  </td>
                  <td className="right mono" onClick={() => navigate(`/hypotheses/${h.id}`)} style={{ cursor: "pointer" }}>
                    {Number(h.daily_budget_rub).toLocaleString("ru-RU")} ₽
                  </td>
                  <td className="right dim mono" onClick={() => navigate(`/hypotheses/${h.id}`)} style={{ cursor: "pointer", fontSize: "0.78rem" }}>
                    {h.expected_clicks_to_verdict}кл / {h.expected_days_to_verdict}д
                    {h.target_roas !== null && <> / ROAS {Number(h.target_roas).toFixed(1)}</>}
                  </td>
                  <td className="dim mono" onClick={() => navigate(`/hypotheses/${h.id}`)} style={{ cursor: "pointer", fontSize: "0.78rem" }}>
                    {formatMSK(h.created_at)}
                  </td>
                  <td>
                    {h.status === "draft" && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!confirm(`Удалить «${h.name}»?`)) return;
                          try {
                            await deleteHypothesis(h.id);
                            load();
                          } catch (err) {
                            alert(`Ошибка: ${err instanceof Error ? err.message : "Unknown"}`);
                          }
                        }}
                        title="Удалить (только для черновиков)"
                        style={{
                          background: "transparent",
                          color: "var(--red)",
                          border: "1px solid transparent",
                          borderRadius: 4,
                          padding: "0.2rem 0.4rem",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                        }}
                      >
                        🗑
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
